<template>
  <div class="tour-overlay" v-if="isActive && tourConfig">
    <!-- Spotlight: visual highlight on target element -->
    <div class="tour-spotlight" :style="spotlightStyle"></div>

    <!-- Tooltip -->
    <TourTooltip
      v-show="!isTransitioning"
      :title="currentStep.title"
      :content="currentStep.content"
      :position="currentStep.position"
      :spotlightRect="tooltipPositionRect"
      :stepIndex="currentStepIndex"
      :totalSteps="totalSteps"
      :isLastStep="isLastStep"
      :isFirstStep="isFirstStep"
      :hideFinish="!!(currentStep.hideFinish)"
      :hidePrev="!!(currentStep.hidePrev)"
      :awaitClick="!!(currentStep.awaitClick)"
      :awaitClickOptional="!!(currentStep.awaitClickOptional)"
      :congrats="!!(currentStep.congrats)"
      :finalCongrats="!!(currentStep.finalCongrats)"
      :aboveDrawer="!!(currentStep.aboveDrawer)"
      :canSkip="currentStep.skipToStep != null"
      @next="nextStep"
      @prev="prevStep"
      @finish="endTour"
      @continue="handleContinue"
      @skip="skipStep"
    />

    <!-- Exit button -->
    <TourExitButton
      v-if="!(currentStep.congrats)"
      :style="currentStep.aboveDrawer ? { zIndex: '10003' } : {}"
      :stepIndex="currentStepIndex"
      :totalSteps="totalSteps"
      :sidebarWidth="sidebarWidth"
      @quit="quitTour"
    />
  </div>
</template>

<script>
import { getTourConfig } from './configs/index'
import TourTooltip from './TourTooltip.vue'
import TourExitButton from './TourExitButton.vue'

const SPOTLIGHT_PADDING = 8
const TRANSITION_DURATION = 350

export default {
  name: 'TourOverlay',

  components: {
    TourTooltip,
    TourExitButton
  },

  data() {
    return {
      targetRect: null,
      tooltipTargetRect: null,
      resizeObserver: null,
      pollInterval: null,
      isTransitioning: false,
      sidebarWidth: 60
    }
  },

  created() {
    // Non-reactive internal state — DOM references and saved styles.
    // Grouped by feature to avoid scattered this._xxx properties.
    this._highlight = {
      el: null,
      originalPosition: '',
      originalZIndex: '',
      originalOverflow: '',
      originalPointerEvents: '',
      blockedEl: null,
      blockedElOriginalPointerEvents: '',
      exceptions: null
    }
    this._awaitClick = {
      el: null,
      elOriginalPointerEvents: '',
      handler: null,
      allEls: null,
      isDelegate: false,
      delegatePoll: null,
      delegatePulseEls: null,
      blockedSiblings: null,
      blockedContainer: null,
      blockedContainerOriginalPointerEvents: ''
    }
    this._pulse = { rings: [] }
    this._blockedClicks = {
      handlers: null,
      onlyHandler: null,
      outsideHandler: null
    }
    this._nav = {
      prevStepAboveDrawer: false,
      stepDirection: 'next'
    }
    this._scrollLock = {
      handlers: []
    }
  },

  computed: {
    isActive() {
      return this.$store.getters['tours/isActive']
    },

    activeTourId() {
      return this.$store.getters['tours/activeTourId']
    },

    currentStepIndex() {
      return this.$store.getters['tours/currentStepIndex']
    },

    tourConfig() {
      if (!this.activeTourId) return null
      return getTourConfig(this.activeTourId)
    },

    totalSteps() {
      return this.tourConfig ? this.tourConfig.steps.length : 0
    },

    currentStep() {
      if (!this.tourConfig) return null
      return this.tourConfig.steps[this.currentStepIndex] || null
    },

    isLastStep() {
      return this.currentStepIndex === this.totalSteps - 1
    },

    isFirstStep() {
      return this.currentStepIndex === 0
    },

    spotlightStyle() {
      if (this.currentStep && this.currentStep.noSpotlight) {
        return {
          top: '0',
          left: '0',
          width: '100vw',
          height: '100vh',
          borderRadius: '0',
          boxShadow: 'none',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }

      if (!this.targetRect) {
        return { display: 'none' }
      }

      const rawPadding = (this.currentStep && this.currentStep.spotlightPadding != null)
        ? this.currentStep.spotlightPadding
        : SPOTLIGHT_PADDING

      const isObjectPadding = typeof rawPadding === 'object' && rawPadding !== null
      const padTop    = isObjectPadding ? (rawPadding.top    != null ? rawPadding.top    : SPOTLIGHT_PADDING) : rawPadding
      const padRight  = isObjectPadding ? (rawPadding.right  != null ? rawPadding.right  : SPOTLIGHT_PADDING) : rawPadding
      const padBottom = isObjectPadding ? (rawPadding.bottom != null ? rawPadding.bottom : SPOTLIGHT_PADDING) : rawPadding
      const padLeft   = isObjectPadding ? (rawPadding.left   != null ? rawPadding.left   : SPOTLIGHT_PADDING) : rawPadding

      const style = {
        top: (this.targetRect.top - padTop) + 'px',
        left: (this.targetRect.left - padLeft) + 'px',
        width: (this.targetRect.width + padLeft + padRight) + 'px',
        height: (this.targetRect.height + padTop + padBottom) + 'px'
      }

      // Raise spotlight above the drawer overlay (z-index 9999)
      if (this.currentStep && this.currentStep.aboveDrawer) {
        style.zIndex = '10001'
      }

      return style
    },

    // Spotlight rect expanded by per-side padding — drives the spotlight visual and the default tooltip position
    paddedRect() {
      if (!this.targetRect) return null
      const step = this.currentStep
      if (!step || step.noSpotlight) return this.targetRect

      const rawPadding = step.spotlightPadding != null ? step.spotlightPadding : SPOTLIGHT_PADDING
      const isObj = typeof rawPadding === 'object' && rawPadding !== null
      const padTop    = isObj ? (rawPadding.top    != null ? rawPadding.top    : SPOTLIGHT_PADDING) : rawPadding
      const padRight  = isObj ? (rawPadding.right  != null ? rawPadding.right  : SPOTLIGHT_PADDING) : rawPadding
      const padBottom = isObj ? (rawPadding.bottom != null ? rawPadding.bottom : SPOTLIGHT_PADDING) : rawPadding
      const padLeft   = isObj ? (rawPadding.left   != null ? rawPadding.left   : SPOTLIGHT_PADDING) : rawPadding

      const r = this.targetRect
      return {
        top: r.top - padTop,
        left: r.left - padLeft,
        right: r.right + padRight,
        bottom: r.bottom + padBottom,
        width: r.width + padLeft + padRight,
        height: r.height + padTop + padBottom
      }
    },

    // Rect used to position the tooltip: uses tooltipTargetRect when step.tooltipTarget is set, otherwise paddedRect
    tooltipPositionRect() {
      return this.tooltipTargetRect || this.paddedRect
    }
  },

  watch: {
    isActive(active) {
      if (active) {
        this.focusTarget()
        this.startListeners()
        this.injectTourContent()
      } else {
        this.cleanup()
      }
    },

    currentStepIndex() {
      this.focusTarget()
    },

    // Watch route changes to chain tours when user navigates (e.g. clicking a calendar date)
    '$route'(to) {
      if (!this.isActive || !this.currentStep || !this.currentStep.nextTour) return
      const nextConfig = getTourConfig(this.currentStep.nextTour)
      if (nextConfig && to.name === nextConfig.route) {
        const nextTour = this.currentStep.nextTour
        this.clearTargetHighlight()
        this.clearDayHighlights()
        this.clearAwaitClick()
        this.$store.dispatch('tours/endTour')
        this.$nextTick(() => {
          this.$store.dispatch('tours/startTour', nextTour)
        })
      }
    }
  },

  mounted() {
    // Restore tour on page refresh
    this.$store.dispatch('tours/restoreTour')

    if (this.isActive) {
      this.$nextTick(() => {
        this.focusTarget()
        this.startListeners()
        this.injectTourContent()
      })
    }
  },

  beforeDestroy() {
    this.cleanup()
  },

  methods: {
    focusTarget() {
      if (!this.currentStep) return

      // Mark as transitioning to hide tooltip until spotlight settles
      this.isTransitioning = true

      // If previous step was inside the drawer but current is not, close the drawer
      if (this._nav.prevStepAboveDrawer && !(this.currentStep.aboveDrawer)) {
        const closeBtn = document.querySelector('.post-drawer .drawer-header .fa-xmark-large')
        if (closeBtn) closeBtn.click()
      }
      this._nav.prevStepAboveDrawer = !!(this.currentStep.aboveDrawer)

      // Clear previous target styles, any day/awaitClick highlights, and scroll locks
      this.clearTargetHighlight()
      this.clearDayHighlights()
      this.clearAwaitClick()
      this.clearBlockedClicks()
      this.clearScrollLock()

      // Reset spotlight position — prevents the old step's spotlight from lingering
      // while waiting for the new step's target to appear (e.g. during file picker wait)
      this.targetRect = null

      // No-spotlight steps (e.g. congrats): skip element targeting
      if (this.currentStep.noSpotlight) {
        setTimeout(() => {
          this.targetRect = { top: 0, left: 0, width: 0, height: 0, right: 0, bottom: 0 }
          this.isTransitioning = false
          if (this.currentStep && this.currentStep.blockOutsideSpotlight) {
            this.setupBlockOutsideSpotlight(null)
          }
        }, TRANSITION_DURATION)
        return
      }

      // Determine max wait: waitForTarget allows longer timeout for async data
      // skipIfMissing / skipIfMissingToStep use a short timeout so missing steps are skipped quickly
      const maxAttempts = this.currentStep.waitForTarget ? 200 : ((this.currentStep.skipIfMissing || this.currentStep.skipIfMissingToStep != null) ? 1 : 20)

      const _startWaiting = () => {
        this.waitForElement(this.currentStep.target, (el) => {
          // skipIfPresent: auto-advance if a given selector is already present in the DOM
          if (this.currentStep && this.currentStep.skipIfPresent && document.querySelector(this.currentStep.skipIfPresent)) {
            this.isTransitioning = false
            this.nextStep()
            return
          }

          // clickOnFound: click the first found selector right when the target appears
          // (e.g. switch a network tab after a video is uploaded, overriding any auto-switch that
          // happened during the upload). Runs before exitIfMissing so conditions are checked
          // with the correct active tab already in place.
          // clickOnFoundRequires: only fire when this additional selector is also present.
          if (this.currentStep && this.currentStep.clickOnFound) {
            const meetsRequirement = !this.currentStep.clickOnFoundRequires
              || document.querySelector(this.currentStep.clickOnFoundRequires)
            if (meetsRequirement) {
              const selectors = Array.isArray(this.currentStep.clickOnFound)
                ? this.currentStep.clickOnFound
                : [this.currentStep.clickOnFound]
              for (const sel of selectors) {
                const clickEl = document.querySelector(sel)
                if (clickEl) { clickEl.click(); break }
              }
            }
          }

          // exitIfMissing: target found but a required secondary selector is absent — jump to specific step
          // Supports a single { selector, toStep } or an array checked in order (first failing condition wins)
          if (this.currentStep && this.currentStep.exitIfMissing) {
            const conds = Array.isArray(this.currentStep.exitIfMissing)
              ? this.currentStep.exitIfMissing
              : [this.currentStep.exitIfMissing]
            for (const cond of conds) {
              if (!document.querySelector(cond.selector)) {
                this.isTransitioning = false
                this.$store.dispatch('tours/goToStep', cond.toStep)
                return
              }
            }
          }

          // Scroll element into view.
          // Skip the early scroll when clickBeforeTarget is set — the tab switch causes
          // an async Vue re-render that resets scrollTop AFTER this call, making it
          // useless. _finalize (after TRANSITION_DURATION settle) handles the scroll instead.
          // scrollToBottom: force container to its absolute bottom immediately — avoids
          // the smooth-center scroll fighting against a bottom-of-form target.
          // poll interval will keep re-applying this in case Vue re-renders after.
          if (this.currentStep.scrollTo === 'top') {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          } else if (this.currentStep.scrollToBottom) {
            const _scrollEl = el.closest('.container-form-scroll') || document.querySelector('.container-form-scroll')
            if (_scrollEl) { _scrollEl.style.scrollBehavior = 'auto'; _scrollEl.scrollTop = _scrollEl.scrollHeight; _scrollEl.style.scrollBehavior = '' }
          } else if (this.currentStep.scrollTo !== 'bottom' && !this.currentStep.clickBeforeTarget) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }

          // Wait for layout to settle before computing position.
          // When clickBeforeTarget was used, the async Vue re-render of the new tab content
          // may outlast TRANSITION_DURATION. Use a longer settle delay in that case so that
          // _finalize's scrollIntoView fires on a stable DOM.
          const _settleDelay = this.currentStep.clickBeforeTarget
            ? Math.max(TRANSITION_DURATION, (this.currentStep.clickBeforeTargetDelay || 300) + 200)
            : TRANSITION_DURATION
          setTimeout(() => {
            const _finalize = (liveEl) => {
              if (liveEl && this.currentStep && this.currentStep.scrollToBottom) {
                // Target is at the very bottom of the scroll container — force scrollTop to
                // the maximum so the element is guaranteed to be visible regardless of layout
                // settle timing (e.g. TikTok form expanding after clickBeforeTarget).
                const scrollEl = liveEl.closest('.container-form-scroll')
                  || document.querySelector('.container-form-scroll')
                if (scrollEl) {
                  scrollEl.style.scrollBehavior = 'auto'
                  scrollEl.scrollTop = scrollEl.scrollHeight
                  scrollEl.style.scrollBehavior = ''
                }
              } else if (liveEl && !this.currentStep.scrollTo) {
                // Re-scroll after layout settle — no-op when element is already fully visible.
                liveEl.scrollIntoView({ behavior: 'instant', block: 'nearest' })
              }
              this.highlightTarget(liveEl)
              this.updateTargetRect(liveEl)
              this.isTransitioning = false
              if (this.currentStep && this.currentStep.highlightDays) {
                this.applyDayHighlights()
              }
              if (this.currentStep && this.currentStep.awaitClick) {
                this.setupAwaitClick(this.currentStep.awaitClick)
              }
              if (this.currentStep && this.currentStep.blockClickSelectors) {
                this.setupBlockedClicks(this.currentStep.blockClickSelectors)
              }
              if (this.currentStep && this.currentStep.blockClickOnlySelectors) {
                this.setupBlockedClicksOnly(this.currentStep.blockClickOnlySelectors)
              }
              if (this.currentStep && this.currentStep.blockOutsideSpotlight) {
                this.setupBlockOutsideSpotlight(this.currentStep.target, this.currentStep.watchTargets)
              }
              if (this.currentStep && this.currentStep.lockScroll) {
                this.applyScrollLock(this.currentStep.lockScroll)
              }
            }

            _finalize(el)
          }, _settleDelay)
        }, maxAttempts, () => {
          // Target not found — skip step when skipIfMissing or skipIfMissingToStep is set
          if (this.currentStep && (this.currentStep.skipIfMissing || this.currentStep.skipIfMissingToStep != null)) {
            this.isTransitioning = false
            if (this._nav.stepDirection === 'prev') {
              this.prevStep()
            } else if (this.currentStep.skipIfMissingToStep != null) {
              this.$store.dispatch('tours/goToStep', this.currentStep.skipIfMissingToStep)
            } else {
              this.nextStep()
            }
          }
        })
      }

      // clickBeforeTarget: click first found selector before waiting for the target (e.g. switch network tab)
      if (this.currentStep.clickBeforeTarget) {
        const selectors = Array.isArray(this.currentStep.clickBeforeTarget)
          ? this.currentStep.clickBeforeTarget
          : [this.currentStep.clickBeforeTarget]
        for (const sel of selectors) {
          const clickEl = document.querySelector(sel)
          if (clickEl) { clickEl.click(); break }
        }
        const delay = this.currentStep.clickBeforeTargetDelay != null
          ? this.currentStep.clickBeforeTargetDelay
          : 300
        setTimeout(_startWaiting, delay)
      } else {
        _startWaiting()
      }
    },

    waitForElement(selector, callback, maxAttempts = 20, onTimeout = null) {
      // Convert legacy attempt count to ms (150ms per attempt) for backward compat
      const timeout = maxAttempts * 150

      // Fast path: element already in the DOM
      const existing = document.querySelector(selector)
      if (existing) { callback(existing); return }

      // Use MutationObserver to react instantly when the element appears
      const observer = new MutationObserver(() => {
        const el = document.querySelector(selector)
        if (el) {
          observer.disconnect()
          clearTimeout(timer)
          callback(el)
        }
      })
      observer.observe(document.body, { childList: true, subtree: true, attributes: true })

      const timer = setTimeout(() => {
        observer.disconnect()
        // Last-chance check (element may exist but no mutation fired, e.g. v-show toggle)
        const el = document.querySelector(selector)
        if (el) {
          callback(el)
        } else if (onTimeout) {
          onTimeout()
        }
      }, timeout)
    },

    highlightTarget(el) {
      // Save original styles to restore later
      this._highlight.el = el
      this._highlight.originalPosition = el.style.position
      this._highlight.originalZIndex = el.style.zIndex
      this._highlight.originalOverflow = el.style.overflow
      this._highlight.originalPointerEvents = el.style.pointerEvents

      const computedPosition = window.getComputedStyle(el).position
      if (computedPosition === 'static') {
        el.style.position = 'relative'
      }
      // Use higher z-index for elements inside the drawer (z-index 9999)
      const zIndex = (this.currentStep && this.currentStep.aboveDrawer) ? '10002' : '9992'
      el.style.zIndex = zIndex
      el.style.overflow = 'visible'

      // Block all clicks on the spotlighted element or a specific selector
      if (this.currentStep && this.currentStep.blockTarget) {
        if (typeof this.currentStep.blockTarget === 'string') {
          const blockEl = document.querySelector(this.currentStep.blockTarget)
          if (blockEl) {
            this._highlight.blockedEl = blockEl
            this._highlight.blockedElOriginalPointerEvents = blockEl.style.pointerEvents
            blockEl.style.pointerEvents = 'none'
          }
        } else {
          el.style.pointerEvents = 'none'
        }

        // Re-enable specific children inside the blocked target
        if (this.currentStep.blockTargetException) {
          const exceptions = Array.isArray(this.currentStep.blockTargetException)
            ? this.currentStep.blockTargetException
            : [this.currentStep.blockTargetException]
          this._highlight.exceptions = []
          exceptions.forEach(sel => {
            const exEls = document.querySelectorAll(sel)
            exEls.forEach(exEl => {
              this._highlight.exceptions.push({ el: exEl, original: exEl.style.pointerEvents })
              exEl.style.pointerEvents = 'auto'
            })
          })
        }
      }
    },

    clearTargetHighlight() {
      if (this._highlight.exceptions) {
        this._highlight.exceptions.forEach(({ el, original }) => {
          el.style.pointerEvents = original || ''
        })
        this._highlight.exceptions = null
      }
      if (this._highlight.blockedEl) {
        this._highlight.blockedEl.style.pointerEvents = this._highlight.blockedElOriginalPointerEvents || ''
        this._highlight.blockedEl = null
      }
      if (this._highlight.el) {
        this._highlight.el.style.position = this._highlight.originalPosition || ''
        this._highlight.el.style.zIndex = this._highlight.originalZIndex || ''
        this._highlight.el.style.overflow = this._highlight.originalOverflow || ''
        this._highlight.el.style.pointerEvents = this._highlight.originalPointerEvents || ''
        this._highlight.el = null
      }
    },

    updateTargetRect(el) {
      if (!el) return
      const mainRect = el.getBoundingClientRect()
      let top = mainRect.top
      let left = mainRect.left
      let right = mainRect.right
      let bottom = mainRect.bottom

      // Expand spotlight to include visible watchTargets
      if (this.currentStep && this.currentStep.watchTargets) {
        for (const selector of this.currentStep.watchTargets) {
          const watchEls = document.querySelectorAll(selector)
          watchEls.forEach(watchEl => {
            if (this.isElementVisible(watchEl)) {
              const watchRect = watchEl.getBoundingClientRect()
              top = Math.min(top, watchRect.top)
              left = Math.min(left, watchRect.left)
              right = Math.max(right, watchRect.right)
              bottom = Math.max(bottom, watchRect.bottom)
            }
          })
        }
      }

      this.targetRect = {
        top,
        left,
        width: right - left,
        height: bottom - top,
        right,
        bottom
      }

      // If the step defines a separate tooltipTarget, track its rect for tooltip positioning
      if (this.currentStep && this.currentStep.tooltipTarget) {
        const tooltipEl = document.querySelector(this.currentStep.tooltipTarget)
        if (tooltipEl) {
          const tr = tooltipEl.getBoundingClientRect()
          this.tooltipTargetRect = { top: tr.top, left: tr.left, right: tr.right, bottom: tr.bottom, width: tr.width, height: tr.height }
        }
      } else {
        this.tooltipTargetRect = null
      }
    },

    isElementVisible(el) {
      if (!el) return false
      const style = window.getComputedStyle(el)
      return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetWidth > 0
    },

    startListeners() {
      this.measureSidebar()
      window.addEventListener('resize', this.onViewportChange)
      window.addEventListener('scroll', this.onViewportChange, true)

      // Poll for target position changes (handles layout shifts).
      // Also re-scrolls the element into view for scrollTo: 'bottom' steps so that any
      // Vue-triggered scrollTop reset (e.g. after a tab switch re-render) is corrected
      // automatically within the next poll tick instead of staying stuck off-screen.
      this.pollInterval = setInterval(() => {
        if (this._highlight.el && this._highlight.el.isConnected) {
          // Re-scroll BEFORE updating the rect so the spotlight lands on the correct position.
          if (!this.isTransitioning && this.currentStep) {
            if (this.currentStep.scrollToBottom) {
              // Keep the container scrolled to the absolute bottom — self-heals any
              // Vue-triggered scrollTop resets caused by async content rendering.
              const _pollScrollEl = this._highlight.el.closest('.container-form-scroll')
                || document.querySelector('.container-form-scroll')
              if (_pollScrollEl) {
                _pollScrollEl.style.scrollBehavior = 'auto'
                _pollScrollEl.scrollTop = _pollScrollEl.scrollHeight
                _pollScrollEl.style.scrollBehavior = ''
              }
            }
          }
          this.updateTargetRect(this._highlight.el)
          this._pulse.rings.forEach(({ ring, el }) => {
            if (el.isConnected) this._updatePulseRingPosition(ring, el)
          })
        }
      }, 500)
    },

    measureSidebar() {
      const sidebar = document.querySelector('.tabs')
      if (sidebar) {
        this.sidebarWidth = sidebar.offsetWidth
      }
    },

    onViewportChange() {
      this.measureSidebar()
      if (this._highlight.el && this._highlight.el.isConnected) {
        this.updateTargetRect(this._highlight.el)
      }
      this._pulse.rings.forEach(({ ring, el }) => {
        if (el.isConnected) this._updatePulseRingPosition(ring, el)
      })
    },

    nextStep() {
      this._nav.stepDirection = 'next'
      const step = this.currentStep
      if (step && step.clickBeforeNextIf) {
        const items = Array.isArray(step.clickBeforeNextIf)
          ? step.clickBeforeNextIf
          : [step.clickBeforeNextIf]
        this._processClickBeforeNext(items, 0, () => {
          if (step.nextToStep != null) {
            this.$store.dispatch('tours/goToStep', step.nextToStep)
          } else {
            this.$store.dispatch('tours/nextStep', this.totalSteps)
          }
        })
        return
      }
      if (step && step.nextToStep != null) {
        this.$store.dispatch('tours/goToStep', step.nextToStep)
        return
      }
      this.$store.dispatch('tours/nextStep', this.totalSteps)
    },

    // Process clickBeforeNextIf items sequentially, with optional delay between each
    _processClickBeforeNext(items, index, done) {
      if (index >= items.length) { done(); return }
      const item = items[index]
      const el = document.querySelector(item.selector)
      const matches = el && (
        item.containsText == null || el.textContent.includes(item.containsText)
      )
      if (matches) {
        const clickEl = item.clickSelector ? document.querySelector(item.clickSelector) : el
        if (clickEl) clickEl.click()
        const delay = item.delayAfter != null ? item.delayAfter : 300
        setTimeout(() => this._processClickBeforeNext(items, index + 1, done), delay)
      } else {
        this._processClickBeforeNext(items, index + 1, done)
      }
    },


    skipStep() {
      const step = this.currentStep
      if (!step || step.skipToStep == null) return
      this._nav.stepDirection = 'next'
      this.$store.dispatch('tours/goToStep', step.skipToStep)
    },

    prevStep() {
      this._nav.stepDirection = 'prev'
      const step = this.currentStep
      if (step && step.clickBeforePrevIf) {
        const items = Array.isArray(step.clickBeforePrevIf)
          ? step.clickBeforePrevIf
          : [step.clickBeforePrevIf]
        this._processClickBeforeNext(items, 0, () => {
          if (step.prevToStep != null) {
            this.$store.dispatch('tours/goToStep', step.prevToStep)
          } else {
            this.$store.dispatch('tours/prevStep')
          }
        })
        return
      }
      if (step && step.prevToStep != null) {
        this.$store.dispatch('tours/goToStep', step.prevToStep)
      } else {
        this.$store.dispatch('tours/prevStep')
      }
    },

    endTour() {
      this.clearTargetHighlight()
      this.clearDayHighlights()
      this.clearAwaitClick()
      this.$store.dispatch('tours/endTour')
    },

    applyDayHighlights() {
      // Inject keyframes + rule targeting the inner frame of non-past day cells.
      // Targeting .day-cell (body cells only, not column headers) keeps
      // day-name rows untouched. Using the parent .planner class means the rule
      // applies automatically when the calendar re-renders on month navigation.
      if (!document.getElementById('tour-day-highlight-style')) {
        const style = document.createElement('style')
        style.id = 'tour-day-highlight-style'
        style.textContent = [
          '@keyframes tour-day-available-pulse {',
          '  0%, 100% {',
          '    box-shadow: inset 0 0 0 1.5px rgba(182, 73, 133, 0);',
          '    background-color: rgba(182, 73, 133, 0);',
          '  }',
          '  50% {',
          '    box-shadow: inset 0 0 0 1.5px rgba(182, 73, 133, 0.55);',
          '    background-color: rgba(182, 73, 133, 0.07);',
          '  }',
          '}',
          '.tour-highlight-days .day-cell:not(.past) {',
          '  animation: tour-day-available-pulse 2s cubic-bezier(0.45, 0, 0.55, 1) infinite !important;',
          '}'
        ].join('\n')
        document.head.appendChild(style)
      }
      const planner = document.querySelector('.planner')
      if (planner) planner.classList.add('tour-highlight-days')
    },

    clearDayHighlights() {
      const style = document.getElementById('tour-day-highlight-style')
      if (style) style.remove()
      const planner = document.querySelector('.planner')
      if (planner) planner.classList.remove('tour-highlight-days')
    },

    setupAwaitClick(selector) {
      // When awaitClickDelegate is set, use event delegation immediately
      // (resilient to DOM re-renders and elements that don't exist yet)
      if (this.currentStep && this.currentStep.awaitClickDelegate) {
        this._awaitClick.delegatePulseEls = []
        const applyPulseToAll = () => {
          document.querySelectorAll(selector).forEach(el => {
            if (!el.classList.contains('tour-await-click-pulse')) {
              if (!this.currentStep || this.currentStep.pulse !== false) this.applyAwaitClickPulse(el)
              this._awaitClick.delegatePulseEls.push(el)
            }
          })
        }
        applyPulseToAll()
        this._awaitClick.delegatePoll = setInterval(applyPulseToAll, 500)
        this._awaitClick.handler = (e) => {
          const target = e.target.closest && e.target.closest(selector)
          if (!target) return
          this.clearAwaitClick()
          if (!this.currentStep) return
          if (this.currentStep.nextTour) {
            this.chainToNextTour(this.currentStep.nextTour)
            return
          }
          const delay = (this.currentStep && this.currentStep.awaitClickAdvanceDelay) || 0
          if (delay) {
            setTimeout(() => this.nextStep(), delay)
          } else {
            this.nextStep()
          }
        }
        document.addEventListener('click', this._awaitClick.handler, true)
        this._awaitClick.isDelegate = true
        return
      }

      this.waitForElement(selector, (firstEl) => {
        // When awaitClickAll is set, listen on ALL matching elements
        if (this.currentStep && this.currentStep.awaitClickAll) {
          const allEls = document.querySelectorAll(selector)
          if (allEls.length === 0) return
          this._awaitClick.allEls = Array.from(allEls)
          this._awaitClick.handler = () => {
            this.clearAwaitClick()
            if (!this.currentStep) return
            if (this.currentStep.nextTour) {
              this.chainToNextTour(this.currentStep.nextTour)
              return
            }
            const delay = (this.currentStep && this.currentStep.awaitClickAdvanceDelay) || 0
            if (delay) {
              setTimeout(() => this.nextStep(), delay)
            } else {
              this.nextStep()
            }
          }
          this._awaitClick.allEls.forEach(awaitEl => {
            if (this.currentStep.pulse !== false) this.applyAwaitClickPulse(awaitEl)
            awaitEl.addEventListener('click', this._awaitClick.handler, { once: true })
          })
          return
        }

        // When awaitClickLast is set, target the last matching element and block others
        let el = firstEl
        if (this.currentStep && this.currentStep.awaitClickLast) {
          const allEls = document.querySelectorAll(selector)
          if (allEls.length > 0) {
            el = allEls[allEls.length - 1]
            // Block all other matching elements
            this._awaitClick.blockedSiblings = []
            allEls.forEach((other) => {
              if (other !== el) {
                this._awaitClick.blockedSiblings.push({ el: other, original: other.style.pointerEvents })
                other.style.pointerEvents = 'none'
              }
            })
          }
        }

        this._awaitClick.el = el
        if (!this.currentStep || this.currentStep.pulse !== false) this.applyAwaitClickPulse(el)

        // Block all sibling interactions in the container, leaving only the target clickable
        if (this.currentStep && this.currentStep.blockTargetSiblings && this.currentStep.target) {
          const container = document.querySelector(this.currentStep.target)
          if (container) {
            this._awaitClick.blockedContainer = container
            this._awaitClick.blockedContainerOriginalPointerEvents = container.style.pointerEvents
            this._awaitClick.elOriginalPointerEvents = el.style.pointerEvents
            container.style.pointerEvents = 'none'
            // Re-enable only the await-click target — CSS allows this even with parent blocked
            el.style.pointerEvents = 'auto'
          }
        }

        this._awaitClick.handler = () => {
          this.clearAwaitClick()
          if (!this.currentStep) return
          if (this.currentStep.nextTour) {
            this.chainToNextTour(this.currentStep.nextTour)
            return
          }
          // Optional delay to let UI react (e.g. drawer opening) before advancing
          const delay = (this.currentStep && this.currentStep.awaitClickAdvanceDelay) || 0
          if (delay) {
            setTimeout(() => this.nextStep(), delay)
          } else {
            this.nextStep()
          }
        }
        el.addEventListener('click', this._awaitClick.handler, { once: true })
      })
    },

    clearAwaitClick() {
      this.clearAwaitClickPulse()
      if (this._awaitClick.delegatePoll) {
        clearInterval(this._awaitClick.delegatePoll)
        this._awaitClick.delegatePoll = null
      }
      if (this._awaitClick.isDelegate && this._awaitClick.handler) {
        document.removeEventListener('click', this._awaitClick.handler, true)
        this._awaitClick.isDelegate = false
      }
      if (this._awaitClick.delegatePulseEls) {
        this._awaitClick.delegatePulseEls.forEach(el => el.classList.remove('tour-await-click-pulse'))
        this._awaitClick.delegatePulseEls = null
      }
      if (this._awaitClick.allEls && this._awaitClick.handler) {
        this._awaitClick.allEls.forEach(el => {
          el.removeEventListener('click', this._awaitClick.handler)
        })
        this._awaitClick.allEls = null
      }
      if (this._awaitClick.el && this._awaitClick.handler) {
        this._awaitClick.el.removeEventListener('click', this._awaitClick.handler)
      }
      if (this._awaitClick.blockedContainer) {
        this._awaitClick.blockedContainer.style.pointerEvents = this._awaitClick.blockedContainerOriginalPointerEvents || ''
        this._awaitClick.blockedContainer = null
      }
      if (this._awaitClick.blockedSiblings) {
        this._awaitClick.blockedSiblings.forEach(({ el, original }) => {
          el.style.pointerEvents = original || ''
        })
        this._awaitClick.blockedSiblings = null
      }
      if (this._awaitClick.el) {
        this._awaitClick.el.style.pointerEvents = this._awaitClick.elOriginalPointerEvents || ''
      }
      this._awaitClick.el = null
      this._awaitClick.handler = null
    },

    applyAwaitClickPulse(el) {
      // Skip if a ring is already tracking this exact element
      if (this._pulse.rings.some(p => p.el === el)) return

      // Inject the keyframe style once
      if (!document.getElementById('tour-btn-pulse-style')) {
        const style = document.createElement('style')
        style.id = 'tour-btn-pulse-style'
        style.textContent = [
          '@keyframes tour-ring-pulse {',
          '  0%   { box-shadow: 0 0 0 0px rgba(182,73,133,0.55); }',
          '  65%  { box-shadow: 0 0 0 5px rgba(182,73,133,0);    }',
          '  100% { box-shadow: 0 0 0 0px rgba(182,73,133,0);    }',
          '}',
          '.tour-pulse-ring {',
          '  position: fixed;',
          '  pointer-events: none;',
          '  z-index: 99999;',
          '  border: 2px solid rgba(182,73,133,0.8);',
          '  box-sizing: border-box;',
          '  animation: tour-ring-pulse 2.4s cubic-bezier(0.215,0.61,0.355,1) infinite;',
          '}'
        ].join('\n')
        document.head.appendChild(style)
      }

      // Create a fixed ring appended to <body> — outside all clipping ancestors
      const ring = document.createElement('div')
      ring.className = 'tour-pulse-ring'
      this._updatePulseRingPosition(ring, el)
      document.body.appendChild(ring)
      this._pulse.rings.push({ ring, el })
    },

    _updatePulseRingPosition(ring, el) {
      const r = el.getBoundingClientRect()
      const pad = 3
      ring.style.top    = (r.top    - pad) + 'px'
      ring.style.left   = (r.left   - pad) + 'px'
      ring.style.width  = (r.width  + pad * 2) + 'px'
      ring.style.height = (r.height + pad * 2) + 'px'
      const br = getComputedStyle(el).borderRadius
      ring.style.borderRadius = br && br !== '0px' ? br : '8px'
    },

    clearAwaitClickPulse() {
      const style = document.getElementById('tour-btn-pulse-style')
      if (style) style.remove()
      // Remove every ring tracked in this step (querySelector only finds the first — use the array)
      this._pulse.rings.forEach(({ ring }) => ring.remove())
      this._pulse.rings = []
      // Safety net: remove any orphaned rings left by edge-cases
      document.querySelectorAll('.tour-pulse-ring').forEach(r => r.remove())
    },

    setupBlockedClicks(selectors) {
      this._blockedClicks.handlers = []
      const handler = (e) => {
        // Allow events that originate from a scheduled post (.container-event)
        if (e.target.closest && e.target.closest('.container-event')) return
        e.stopPropagation()
        e.preventDefault()
      }
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          // Block both mousedown (FullCalendar select interaction) and click
          el.addEventListener('mousedown', handler, true)
          el.addEventListener('click', handler, true)
          this._blockedClicks.handlers.push({ el, handler })
        })
      })
    },

    clearBlockedClicks() {
      if (this._blockedClicks.handlers) {
        this._blockedClicks.handlers.forEach(({ el, handler }) => {
          el.removeEventListener('mousedown', handler, true)
          el.removeEventListener('click', handler, true)
        })
        this._blockedClicks.handlers = null
      }
      if (this._blockedClicks.onlyHandler) {
        document.removeEventListener('click', this._blockedClicks.onlyHandler, true)
        this._blockedClicks.onlyHandler = null
      }
      if (this._blockedClicks.outsideHandler) {
        document.removeEventListener('click', this._blockedClicks.outsideHandler, true)
        document.removeEventListener('mousedown', this._blockedClicks.outsideHandler, true)
        this._blockedClicks.outsideHandler = null
      }
    },

    applyScrollLock(selector) {
      // Attach non-passive wheel/touch listeners to prevent user scroll on the given
      // container(s) while the step is active — stops accidental scroll from drifting
      // the spotlight off its target. Programmatic scrollTop changes still work.
      const selectors = Array.isArray(selector) ? selector : [selector]
      selectors.forEach(sel => {
        const lockEl = document.querySelector(sel)
        if (!lockEl) return
        const handler = (e) => { e.preventDefault() }
        lockEl.addEventListener('wheel', handler, { passive: false })
        lockEl.addEventListener('touchmove', handler, { passive: false })
        this._scrollLock.handlers.push({ el: lockEl, handler })
      })
    },

    clearScrollLock() {
      this._scrollLock.handlers.forEach(({ el, handler }) => {
        el.removeEventListener('wheel', handler)
        el.removeEventListener('touchmove', handler)
      })
      this._scrollLock.handlers = []
    },

    setupBlockOutsideSpotlight(targetSelector, watchTargets) {
      // Capture-phase listener that blocks all clicks outside the spotlight target and tour UI
      const handler = (e) => {
        // Allow clicks inside the spotlight target (if defined)
        if (targetSelector && e.target.closest && e.target.closest(targetSelector)) return
        // Allow clicks on tour UI (tooltip, exit button)
        if (e.target.closest && e.target.closest('.tour-overlay')) return
        // Allow clicks inside watchTargets (visually included in the spotlight)
        if (watchTargets) {
          for (const sel of watchTargets) {
            if (e.target.closest && e.target.closest(sel)) return
          }
        }
        e.stopPropagation()
        e.preventDefault()
      }
      document.addEventListener('click', handler, true)
      document.addEventListener('mousedown', handler, true)
      this._blockedClicks.outsideHandler = handler
    },

    setupBlockedClicksOnly(selectors) {
      // Use delegation on a stable parent — FullCalendar recreates DOM elements after drag-and-drop
      this._blockedClicks.onlyHandler = (e) => {
        for (const sel of selectors) {
          if (e.target.closest && e.target.closest(sel)) {
            e.stopPropagation()
            e.preventDefault()
            return
          }
        }
      }
      document.addEventListener('click', this._blockedClicks.onlyHandler, true)
    },

    chainToNextTour(nextTourId) {
      const nextConfig = getTourConfig(nextTourId)
      if (!nextConfig) return
      this.clearTargetHighlight()
      this.clearDayHighlights()
      this.clearBlockedClicks()
      this.$store.dispatch('tours/endTour')
      this.$router.push({ name: nextConfig.route })
      this.$nextTick(() => {
        this.$store.dispatch('tours/startTour', nextTourId)
      })
    },

    handleContinue() {
      // Yes button on congrats step — chain to next tour if defined
      const nextTourId = this.currentStep && this.currentStep.nextTour
      this.cleanup()
      this.$store.dispatch('tours/endTour')
      if (nextTourId) {
        const nextConfig = getTourConfig(nextTourId)
        if (nextConfig) {
          this.$router.push({ name: nextConfig.route })
          this.$nextTick(() => {
            this.$store.dispatch('tours/startTour', nextTourId)
          })
          return
        }
      }
    },

    quitTour() {
      this.clearTargetHighlight()
      this.clearDayHighlights()
      this.clearAwaitClick()
      this.clearBlockedClicks()
      this.clearInjectedContent()
      this.$store.dispatch('tours/quitTour')
    },

    cleanup() {
      this.clearTargetHighlight()
      this.clearDayHighlights()
      this.clearAwaitClick()
      this.clearBlockedClicks()
      this.clearScrollLock()
      this.clearInjectedContent()
      window.removeEventListener('resize', this.onViewportChange)
      window.removeEventListener('scroll', this.onViewportChange, true)
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
    },

    injectTourContent() {
      if (!this.tourConfig || !this.tourConfig.injectContent) return
      const config = this.tourConfig.injectContent

      // Inject demo text into the tiptap post content editor
      if (config.text) {
        this.waitForElement('.content-post .ProseMirror[contenteditable]', (el) => {
          el.innerHTML = '<p>' + config.text + '</p>'
          el.dispatchEvent(new Event('input', { bubbles: true }))
        })
      }
    },

    clearInjectedContent() {
      // Reserved for future cleanup if injected content needs reverting
    }
  }
}
</script>

<style scoped>
.tour-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9989;
  pointer-events: none;
}

.tour-spotlight {
  position: fixed;
  z-index: 9991;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  transition: top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease;
}
</style>
